import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useTranslation } from "react-i18next";
import Header from "./Header";
import Footer from "./Footer";
import Sidebar from "./Sidebar";
import remarkGfm from "remark-gfm";
import DownloadModal from "./DownloadModal";

const supportedLanguages = ["en", "fr", "ar", "es", "it"];

const normalizeLanguage = (language) => {
  const normalizedLanguage = language?.split("-")[0]?.toLowerCase() || "en";

  return supportedLanguages.includes(normalizedLanguage)
    ? normalizedLanguage
    : "en";
};

const LegalMarkdownPage = ({
  documents,
  containerClassName,
  headingClassName,
  errorMessage = "This document could not be loaded.",
}) => {
  const { i18n } = useTranslation();

  const [document, setDocument] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [showDownload, setShowDownload] = useState(false);

  const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language);

  useEffect(() => {
    const loadDocument = async () => {
      setIsLoading(true);
      setDocument("");
      setError("");

      try {
        const documentUrl = documents[language] || documents.en;

        const response = await fetch(documentUrl);

        if (!response.ok) {
          throw new Error(`Unable to load document: ${response.status}`);
        }

        const markdown = await response.text();

        if (!markdown.trim()) {
          throw new Error("The document is empty.");
        }

        setDocument(markdown);
      } catch (err) {
        console.error("Legal document loading error:", err);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadDocument();
  }, [documents, language, errorMessage]);

  return (
    <>
      <DownloadModal
        open={showDownload}
        onClose={() => setShowDownload(false)}
      />
      <Header showDownload={showDownload} setShowDownload={setShowDownload} />
      {/*<Sidebar />*/}

      <main
        className={containerClassName}
        lang={language}
        dir={language === "ar" ? "rtl" : "ltr"}
      >
        {isLoading && <p className="section-content">Loading...</p>}

        {!isLoading && error && <p className="legal-document-error">{error}</p>}

        {!isLoading && !error && (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ children, ...props }) => (
                <h1 className={headingClassName} {...props}>
                  {children}
                </h1>
              ),

              h2: ({ children, ...props }) => (
                <h2 className="section-heading" {...props}>
                  {children}
                </h2>
              ),

              h3: ({ children, ...props }) => (
                <h3 className="subsection-heading" {...props}>
                  {children}
                </h3>
              ),

              p: ({ children, ...props }) => (
                <p className="section-content" {...props}>
                  {children}
                </p>
              ),

              li: ({ children, ...props }) => (
                <li className="section-content-list-item" {...props}>
                  {children}
                </li>
              ),

              a: ({ children, ...props }) => (
                <a
                  className="legal-document-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  {...props}
                >
                  {children}
                </a>
              ),

              table: ({ children, ...props }) => (
                <div className="legal-table-wrapper">
                  <table className="legal-document-table" {...props}>
                    {children}
                  </table>
                </div>
              ),

              th: ({ children, ...props }) => (
                <th className="legal-document-table-heading" {...props}>
                  {children}
                </th>
              ),

              td: ({ children, ...props }) => (
                <td className="legal-document-table-cell" {...props}>
                  {children}
                </td>
              ),
            }}
          >
            {document}
          </ReactMarkdown>
        )}
      </main>

      <Footer />
    </>
  );
};

export default LegalMarkdownPage;
